/**
 * The monthly trainer review's four questions — their wording, their options
 * and the codes the answers are stored as (trainer-review-lane, 2026-10-23;
 * migration 249).
 *
 * Mario (23 Oct 2026) chose four tap-only questions, sent one at a time as
 * WhatsApp list messages: the PDF scorecard arrives with Q1, each tap brings
 * the next question, and a thank-you follows Q4. The answers show on
 * /trainer-performance beside his private notes.
 *
 * One copy for every reader: the lane that sends Q1
 * (`trainer_review_sender`), the inbound router that records a tap and sends
 * the next question (`routeTrainerReviewReply` +
 * `record_trainer_review_answer`), and the /trainer-performance panel that
 * prints the answers. The CHECK lists on `trainer_reviews` (migration 249)
 * equal the codes below; `trainer-review-questions.test.ts` pins them.
 *
 * Dependency-free leaf: imported by the SPA, the dashboard server, the
 * canonical actions and the saga runtime alike.
 */

export const TRAINER_REVIEW_QUESTION_NUMBERS = [1, 2, 3, 4] as const;
export type TrainerReviewQuestionNumber =
  (typeof TRAINER_REVIEW_QUESTION_NUMBERS)[number];

export interface TrainerReviewOption {
  /** Stored code: lowercase letters, digits and `_` (it rides in a row id). */
  readonly code: string;
  /** The list row's title — at most 24 characters (WhatsApp's cap). */
  readonly label: string;
}

export interface TrainerReviewQuestion {
  readonly number: TrainerReviewQuestionNumber;
  /** The `trainer_reviews` column the answer is stored in. */
  readonly column: "q1_rating" | "q2_best" | "q3_obstacle" | "q4_topic";
  /** The list message's body, and the panel's row label. */
  readonly text: string;
  readonly options: ReadonlyArray<TrainerReviewOption>;
}

export const TRAINER_REVIEW_QUESTIONS: ReadonlyArray<TrainerReviewQuestion> = [
  {
    number: 1,
    column: "q1_rating",
    text: "How did this month go for you overall?",
    options: [
      { code: "5", label: "5 · Brilliant" },
      { code: "4", label: "4 · Good" },
      { code: "3", label: "3 · OK" },
      { code: "2", label: "2 · Tough" },
      { code: "1", label: "1 · Very tough" },
    ],
  },
  {
    number: 2,
    column: "q2_best",
    text: "What went best this month?",
    options: [
      { code: "client_results", label: "Client results" },
      { code: "retention", label: "Keeping my clients" },
      { code: "trials", label: "Trials & new clients" },
      { code: "selling_packs", label: "Selling packs" },
      { code: "own_skills", label: "My own skills" },
      { code: "teamwork", label: "Teamwork" },
      { code: "other", label: "Something else" },
    ],
  },
  {
    number: 3,
    column: "q3_obstacle",
    text: "What got in your way most?",
    options: [
      { code: "schedule", label: "My hours/schedule" },
      { code: "few_clients", label: "Too few clients" },
      { code: "no_shows", label: "No-shows & cancels" },
      { code: "admin", label: "Admin & systems" },
      { code: "equipment", label: "Equipment / suits" },
      { code: "communication", label: "Communication" },
      { code: "nothing", label: "Nothing really" },
      { code: "other", label: "Something else" },
    ],
  },
  {
    number: 4,
    column: "q4_topic",
    text: "What do you want to cover on our call?",
    options: [
      { code: "numbers", label: "My numbers" },
      { code: "pay", label: "Pay" },
      { code: "schedule", label: "Hours/schedule" },
      { code: "clients", label: "My clients" },
      { code: "growth", label: "Training & growth" },
      { code: "personal", label: "Something personal" },
      { code: "nothing", label: "Nothing specific" },
    ],
  },
];

/** The question numbered `n`. */
export function trainerReviewQuestion(
  n: TrainerReviewQuestionNumber,
): TrainerReviewQuestion {
  const q = TRAINER_REVIEW_QUESTIONS[n - 1];
  if (q === undefined) throw new Error(`trainer_review_question_unknown: ${n}`);
  return q;
}

/** The option `code` of question `n`, or null when it is not one of them. */
export function trainerReviewOption(
  n: TrainerReviewQuestionNumber,
  code: string,
): TrainerReviewOption | null {
  return trainerReviewQuestion(n).options.find((o) => o.code === code) ?? null;
}

/** The list message's header: "Monthly review · 1 of 4". */
export function trainerReviewListHeader(n: TrainerReviewQuestionNumber): string {
  return `Monthly review · ${n} of ${TRAINER_REVIEW_QUESTIONS.length}`;
}

/** The list message's button. */
export const TRAINER_REVIEW_LIST_BUTTON = "Choose an answer" as const;

/**
 * The Continue's subject — `message_waiting`'s {{2}}: "E-Pulsive needs a
 * quick answer from you about your monthly review."
 */
export const TRAINER_REVIEW_HOLD_SUBJECT = "your monthly review" as const;

/** After the fourth answer, once. */
export function trainerReviewThankYou(firstName: string): string {
  const name = firstName.trim();
  return name.length > 0
    ? `Thanks, ${name} — that's all four. We'll be in touch about your review call.`
    : "Thanks — that's all four. We'll be in touch about your review call.";
}

/** When a tap could not be recorded; the next tap is a fresh try. */
export const TRAINER_REVIEW_ANSWER_FAILED_REPLY =
  "Sorry — that didn't go through. Please tap your answer again." as const;
